
import { Camera, Cameras, PerspectiveCamera, defines, CanvasInfo, CameraNavigator, events, ItemManager, WalkThroughAnimationControl, OrbitCameraControl, statsUtils } from "../base";
import { webcadDefines, WebCadItemManager, TopView2D, ThreeView, View3dGlobal, WarpPoint } from "./index";
import * as digifie from "../../../digifie";
import * as THREE from "three";
import * as TWEEN from "@tweenjs/tween.js";
import * as marietta from '../../../marietta';

import * as graphicLibs from '../../graphicLibs';
import { BUILDING_TYPE_MANSION } from '../../../marietta/webcad/Constants';

import { PSSceneManagement } from '../webcad';
import { ObjectType } from "./ObjectType";
interface tweenTransitiondata {
    type: TweenType
    x: number
    y: number
    z: number
    cx?: number
    cy?: number
    cz?: number
    easing?: any
    completed?: TweenCompletion
    compParam?: any
    tFactor?: number
    center?: THREE.Vector3
    delay?: number
}

/* WebCadCameras
 * WebCAD用カメラ管理クラス.
 */
export class WebCadTweenBase {
    private _instance: TWEEN.Tween;
    public get me(): TWEEN.Tween { return this._instance; }
    public set me(i: TWEEN.Tween) { this._instance = i; }

    private camera: Camera;
    private cameras: Cameras;

    constructor(cam: Camera, cams: Cameras) {
        this.camera = cam;
        this.cameras = cams;
    }
    public start() { this.me.start(); }
}

/**
 * class: WebCadTween
 */
export class WebCadTween extends WebCadTweenBase {
    constructor(pos2: THREE.Vector3, cam: Camera, cams: Cameras) {
        super(cam, cams);

        this.me = new TWEEN.Tween(pos2);
    }
}

/**
 * class: wcCameraUtils
 */
export class wcCameraUtils {
    public static calcTweenParam(center: THREE.Vector3, point: THREE.Vector3): THREE.Vector3 {
        const _height = point.y - center.y;
        const _dist = Math.sqrt(Math.pow((point.x - center.x), 2) + Math.pow((point.z - center.z), 2));
        const _angle = Math.atan2(point.x - center.x, point.z - center.z);
        return new THREE.Vector3(_height, _angle, _dist);
    }
    public static calcPosition(center: THREE.Vector3, param: THREE.Vector3): THREE.Vector3 {
        const pos2: THREE.Vector3 = new THREE.Vector3(Math.sin(param.y) * param.z + center.x, param.x, Math.cos(param.y) * param.z + center.z);
        return pos2;
    }
    public static registerWarpPoint(camera: Camera, warp: WarpPoint, offDist: number, setPosition: boolean): void {
        if (warp == undefined) return;
        const pos2 = warp.position.clone();
        const look = warp.lookAt.clone();
        const target = warp.lookAt.clone();
        if (look.x > 0.0001) {
            pos2.x -= offDist;
            target.z = pos2.z;
        }
        else if (look.x < -0.0001) {
            pos2.x += offDist;
            target.z = pos2.z;
        }
        else if (look.z > 0.0001) {
            pos2.z -= offDist;
            target.x = pos2.x;
        }
        else {
            pos2.z += offDist;
            target.x = pos2.x;
        }
        camera.setSetting(warp.name + webcadDefines.WarpPointNames.position, pos2.clone());
        camera.setSetting(warp.name + webcadDefines.WarpPointNames.target, target);
        if (setPosition) {
            camera.position.set(0, 0, 0);
            look.y = 0;
            camera.lookAt = look;
            camera.position = pos2;
        }
    }
}



// PerspectiveCamera 派生クラス.
/* WebCadPerspectiveCamera
 *　パースペクティブカメラ
 */
export class WebCadPerspectiveCamera extends PerspectiveCamera {
    private _itemmgr: any;
    constructor(itemmgr: WebCadItemManager, fov: number, aspect: number, near: number, far: number, cams?: Cameras) {
        super(fov, aspect, near, far, cams);
        this._itemmgr = itemmgr;
    }

    public notify(message: string, param: {}) {
        super.notify(message, param);
        if ((this._itemmgr != undefined) &&
            ((message == defines.EventNames.wtPointChanged) ||
                (message == defines.EventNames.orAngleChanged) ||
                (message == defines.EventNames.cameraReset) ||
                (message == webcadDefines.WebCadEventNames.sizeObjectVisible))) {
            this._itemmgr.updateBillBoards(this);
            this._itemmgr.updateSizeLabels(this);
        }
    }
}

/**
 * class: WebCadWtPerspectiveCamera
 */
export class WebCadWtPerspectiveCamera extends WebCadPerspectiveCamera {
    constructor(itemmgr: WebCadItemManager, fov: number, aspect: number, near: number, far: number, cams?: Cameras) {
        super(itemmgr, fov, aspect, near, far, cams);
    }

    public canvasSizeChanged() {
        const size = CanvasInfo.get().size;
        const pers_cam = <THREE.PerspectiveCamera>(this.me);
        pers_cam.aspect = size.x / size.y;
        this.updateProjectionMatrix();
    }
    public updateProjectionMatrix() {
        const pers_cam = <THREE.PerspectiveCamera>(this.me);
        pers_cam.updateProjectionMatrix();

        if (webcadDefines.WebCadCameraDefs.wtAoriFixRatio != 0.0) {
            const vec: THREE.Vector3 = new THREE.Vector3();
            const dir = pers_cam.getWorldDirection(vec).normalize();
            const len = dir.x * dir.x + dir.z * dir.z;
            if (len > 0.001) {
                const mat1 = new THREE.Matrix4;
                const mat2 = new THREE.Matrix4;
                mat1.elements[4 * 1 + 2] = -webcadDefines.WebCadCameraDefs.wtAoriFixRatio * (-dir.y / len);
                mat2.elements[4 * 2 + 2] = (1.0 + webcadDefines.WebCadCameraDefs.wtAoriFixRatio) - (len * webcadDefines.WebCadCameraDefs.wtAoriFixRatio);
                mat1.multiply(mat2);
                pers_cam.projectionMatrix.multiply(mat1);
            }
        }
    }
    public notify(message: string, param: {}) {
        super.notify(message, param);
        if ((message == defines.EventNames.wtAngleChanged) || (message == defines.EventNames.wtPointChanged)) {
            this.updateProjectionMatrix();
        }
    }
}

// CameraNavigator 派生クラス.
/* WalkThroughCameraNavigator
 *　ウォークスルーカメラナビゲータ
 */
export class WalkThroughCameraNavigator extends CameraNavigator {
    _instance: TopView2D;
    public get me(): TopView2D { return this._instance; }
    public set me(i: TopView2D) { this._instance = i; }

    public get enabled(): boolean { return this.me.visible; }
    public set enabled(value: boolean) { this.me.visible = value; }

    constructor(container: HTMLElement, eventDispatcher?: events.EventDispatcher) {
        super(eventDispatcher);

        this.me = new TopView2D("topview", 20, 50, container);
        this.me.visible = false;
    }
    public notify(message: string, param: any) {
        if (message == defines.EventNames.wtPointChanged) {
            const point2d: { x: number, y: number } = param[webcadDefines.ParamNames.point2d];
            this.me.position = point2d;
        }
        else if (message == defines.EventNames.wtAngleChanged) {
            this.me.rotation = param[webcadDefines.ParamNames.rotation2d];
        }
        else if (message == webcadDefines.WebCadEventNames.wtFloorChanged) {
            const layerNo: number = param[webcadDefines.ParamNames.layerNumber];
            this.me.floor = layerNo;
        }
    }
}

/* WebCadPicker
 *　アイテム入替用のNavigator
 */
export class WebCadPicker extends CameraNavigator {
    private _screenPosUtil: digifie.utils.ScreenPositionUtil;

    constructor(eventDispatcher?: events.EventDispatcher) {
        super(eventDispatcher);
        this._screenPosUtil = new digifie.utils.ScreenPositionUtil();
    }

    public update(cameras: Cameras, camera: Camera, items: ItemManager): void {
        // ThreeViewのイニシャライズ完了前に呼ばれるとエラーとなる為
        // イニシャライズが完了しているかのチェック
        if (ThreeView.exist() == false) return;
        if (ThreeView.get().pickermanager.hasPicker() == false) return;
        if (ThreeView.get().pickermanager.visible() == false) {
            ThreeView.get().pickermanager.hide();
            return;
        }

        const wcmgr = <WebCadItemManager>(items);
        const wcams = <WebCadCameras>(cameras);
        const layer = `L${wcams.floor}`;
        const size = CanvasInfo.get().size;
        const object = ThreeView.get().pickermanager.targetObject();

        const objects = wcmgr.floorsObjects.getObjects(layer);
        const position = this._screenPosUtil.ScreenCoordinates(size, object, camera.me, objects);
        if (position.x < 0 && position.y < 0) {
            // ThreeView.get().pickermanager.hide();
            return;
        }
        // 建具類ならアイコンを表示させない
        if (ObjectType.isFitting(object) == true) {
            ThreeView.get().pickermanager.hide();
            return;
        }
        const p1 = new THREE.Vector2(position.x - ((7+40+7) / 2), position.y - ((40 * 3) / 2));
        ThreeView.get().pickermanager.update(p1);
        // const p2 = new THREE.Vector2(p1.x + (7 + 40 + 7) * 2, p1.y + 40 * 2);
        // ThreeView.get().pickermanager.updateDeletePicker(p2);
    }
}

/* WebCadPicker
 *　アイテム入替用のNavigator
 */
export class WebCadPinPicture extends CameraNavigator {
    private _screenPosUtil: digifie.utils.ScreenPositionUtil;

    constructor(eventDispatcher?: events.EventDispatcher) {
        super(eventDispatcher);
        this._screenPosUtil = new digifie.utils.ScreenPositionUtil();
    }

    public update(cameras: Cameras, camera: Camera, items: ItemManager): void {
        // ThreeViewのイニシャライズ完了前に呼ばれるとエラーとなる為
        // イニシャライズが完了しているかのチェック
        if (ThreeView.exist() == false) return;
        if (ThreeView.get().cameraPinManager.visible() == false) {
            ThreeView.get().cameraPinManager.hide();
            return;
        }

        const wcmgr = <WebCadItemManager>(items);
        const wcams = <WebCadCameras>(cameras);
        const layer = `L${wcams.floor}`;
        const size = CanvasInfo.get().size;
        const object = ThreeView.get().cameraPinManager.targetObject();

        const objects = wcmgr.floorsObjects.getObjects(layer);
        const position = this._screenPosUtil.ScreenCoordinates(size, object, camera.me, objects);

        if (position.x < 0 || position.y < 0) {
            ThreeView.get().cameraPinManager.update(new THREE.Vector2(-100, -100));
            return;
        }

        const pos = ThreeView.get().cameraPinManager.getPosition(object, camera);
        ThreeView.get().cameraPinManager.update(pos);
    }
}

// 3Dメジャーのアップデート
// 1Frame毎に呼ばれる
export class WebCadMeasure3D extends CameraNavigator {
    constructor(eventDispatcher?: events.EventDispatcher) {
        super(eventDispatcher);
    }

    public update(cameras: Cameras, camera: Camera, items: ItemManager): void {
        // ThreeViewのイニシャライズ完了前に呼ばれるとエラーとなる為
        // イニシャライズが完了しているかのチェック
        if (ThreeView.exist() == false) return;

        ThreeView.get().measuremanager.updateMeasure();
    }
}

// 選択状態のBBOXアップデート
// 1Frame毎に呼ばれる
export class WebCadSelectedBBOX extends CameraNavigator {
    constructor(eventDispatcher?: events.EventDispatcher) {
        super(eventDispatcher);
    }

    public update(cameras: Cameras, camera: Camera, items: ItemManager): void {
        // ThreeViewのイニシャライズ完了前に呼ばれるとエラーとなる為
        // イニシャライズが完了しているかのチェック
        if (ThreeView.exist() == false) return;
        if (!ThreeView.get().BBOXSelectedManager) return;
        if (ThreeView.get().ItemDragManager.selectObj != null) {
            ThreeView.get().BBOXSelectedManager.showBox();
            ThreeView.get().BBOXSelectedManager.reSize(ThreeView.get().ItemDragManager.selectObj);
        } else {
            ThreeView.get().BBOXSelectedManager.hideBox();
        }
    }
}

export class mantionMove extends CameraNavigator {
    constructor(private eventDispatcher?: events.EventDispatcher) {
        super(eventDispatcher);
    }

    public update(cameras: Cameras, camera: Camera, items: ItemManager): void {
        // ThreeViewのイニシャライズ完了前に呼ばれるとエラーとなる為
        // イニシャライズが完了しているかのチェック
        if (ThreeView.exist() == false) return;
        if (ThreeView.get().wcItemmgr.outerLine == null) return;
        if (marietta.webcad.WebCADGlobal.webcad2Dmanager.buildingType !== BUILDING_TYPE_MANSION) return;

        const pos = camera.me.getWorldPosition(new THREE.Vector3());
        const res = graphicLibs.utility.pointInArea.pointInArea(new THREE.Vector2(pos.x, pos.z), ThreeView.get().wcItemmgr.outerLine);

        if (res == false) {
            marietta.view3d.webcad.View3dGlobal.shared.homeCamera();

            const entrance = ThreeView.get().wcItemmgr.warps.getFromName(webcadDefines.WarpPointNames.entrance);
            wcCameraUtils.registerWarpPoint(camera, entrance, 0.0, true);

            camera.notify(defines.EventNames.cameraReset, {});
            this.eventDispatcher.dispatchEvent(new events.Event(defines.EventNames.wtPointChanged, camera.position));
            this.eventDispatcher.dispatchEvent(defines.EventNames.wtAngleChanged);
            this.eventDispatcher.dispatchEvent(webcadDefines.WebCadEventNames.wtFloorChanged);
        }
    }
}

// CameraNavigator 派生クラス.
/* WalkThroughAnimationControl
 *　ウォークスルーアニメーションコントローラ
 */
export class WebCadWalkThroughAnimationControl extends WalkThroughAnimationControl {
    constructor(eventDispatcher?: events.EventDispatcher) {
        super(eventDispatcher);

        // if (SystemEnv.islocalhost && (eventDispatcher != undefined)) {
        //     eventDispatcher.addEventListener(defines.EventNames.wtObjectClicked, (e: events.Event) => {
        //         this.objectClicked(e);
        //     });
        //     eventDispatcher.addEventListener(defines.EventNames.orObjectClicked, (e: events.Event) => {
        //         this.objectClicked(e);
        //     });
        // }
    }
    // private objectClicked(e: events.Event) {
    //     const mesh: THREE.Mesh = <THREE.Mesh>(e.value[0].object);
    //     const names = matUtils.materialNames(<any>mesh.material);
    //     const map: THREE.Texture = matUtils.getImageMap(<any>mesh.material);
    //     const url: string = wcItemUtils.getUrl(mesh);
    //     if (map != undefined)
    //         console.log("OBJ:" + mesh.parent.name + ", MAT:" + names + ", MAP:" + map.name + ", FILE:" + url);
    //     else
    //         console.log("OBJ:" + mesh.parent.name + ", MAT:" + names + ", FILE:" + url);
    // }
    /*
     * getTargets(override)
     */
    public getTargets(cameras: Cameras, camera: Camera, items: ItemManager): THREE.Object3D[] {
        const wcItemmgr = <WebCadItemManager>(items);
        const wcCameras = <WebCadCameras>(cameras);
        return wcItemmgr.floorsObjects.getObjects('L' + String(wcCameras.floor));
    }
    /*
     * getTargets(override)
     */
    protected getTriggerName(object: THREE.Object3D): string {
        if (object == undefined) return undefined;
        const obj = <THREE.Object3D>(object);
        if (obj.userData.attribute && obj.userData.attribute.original_name != undefined)
            return object.name;
        return this.getTriggerName(object.parent);
    }
}

// Cameras 派生クラス.
/* WebCadCameras
 *　カメラマネージャー
 */
enum TweenCompletion {
    tweenTransition,
    tweenShowFinished,
    tweenAroundFinished,
    tweenInFinished,
    tweenToFinished,
    tweenFadeInFinished,
    tweenFreeFallFinished,
    tweenLevitationFinished,
    tweenCameraRotateFinished,
    tweenGenericFinished
}
enum TweenType {
    na,
    move,
    rotate,
    target,
    fade,
    lookAt
}

/**
 * class: WebCadCameras
 */
export class WebCadCameras extends Cameras {
    private firstWalk: boolean;
    private secondWalk: boolean;
    public firstShow: boolean;
    private _floor: number;
    public get floor(): number { return this._floor; }
    public set floor(val: number) { this._floor = val; }
    private _opeMode: defines.OpeMode;
    public get opeMode(): defines.OpeMode { return this._opeMode; }
    public set opeMode(mode: defines.OpeMode) { // access limited from ThreeView
        this._opeMode = mode;
        switch (mode) {
            case defines.OpeMode.create:
            case defines.OpeMode.animate:
                this.camera.uiEnabled = false;
                break;
            case defines.OpeMode.operate:
                this.camera.uiEnabled = true;
                break;
        }
    }

    public get super(): Cameras { return this.super; }

    constructor(eventDispatcher?: events.EventDispatcher) {
        super(eventDispatcher);
        this.firstWalk = true;
        this.firstShow = false;

        if (eventDispatcher != undefined) {
            eventDispatcher.addEventListener(defines.EventNames.wtPointChanged, (e: events.Event) => {
                if (e.value != undefined) {
                    const hitPosition: THREE.Vector3 = <THREE.Vector3>(e.value);
                    this.walkThroughPointChanged(hitPosition);
                }
            });
            eventDispatcher.addEventListener(defines.EventNames.wtAngleChanged, (e: events.Event) => {
                this.walkThroughAngleChanged();
                // アイテム選択解除
                // ThreeView.get().unSelectedObject();
                // e.value == trueで内観時のウォークスルーカメラでのwheel移動
                // wheel移動時はItemDragManagerは有効とする
                if (e.value == false) {
                    ThreeView.get().ItemDragManager.enabled = false;
                }

                // pixelstreamingカメラ送信
                new PSSceneManagement().sendCamera();
            });
            eventDispatcher.addEventListener(defines.EventNames.orAngleChanged, (e: events.Event) => {
                this.orbitAngleChanged();
            });
            eventDispatcher.addEventListener(webcadDefines.WebCadEventNames.wtFloorChanged, (e: events.Event) => {
                this.walkThroughFloorChanged();
            });
            eventDispatcher.addEventListener(defines.EventNames.wtFalling, (e: events.Event) => {
                if (e.value != undefined) {
                    const fallTo: number = e.value;
                    this.walkThroughFalling(fallTo);
                }
            });
            eventDispatcher.addEventListener(webcadDefines.WebCadEventNames.sizeObjectVisible, (e: events.Event) => {
                this.sizeObjectVisible(true);
            });
            eventDispatcher.addEventListener(webcadDefines.WebCadEventNames.initialized, (e: events.Event) => {
                this.firstShow = true;
            });
            eventDispatcher.addEventListener(defines.EventNames.wtMouseDown, (e: events.Event) => {
                ThreeView.get().pscommunication.abortTimer();
            });
            eventDispatcher.addEventListener(defines.EventNames.wtEndEvent, (e: events.Event) => {
                console.log('Event End Event');
                if (e.value != undefined) {

                    // pixelstreamingカメラ送信
                    new PSSceneManagement().sendCamera();
                }
                // pixelstreaming有効時にはitemdragmanagerを有効にしない
                if (ThreeView.get().isPSVideoMode == false) {
                    ThreeView.get().ItemDragManager.enabled = true;
                }
                // 画角内判定
                ThreeView.get().CameraTarget.getTargets(ThreeView.get().wcCameras.camera.me, ThreeView.get().OctreeManager.nodeStatus);
            });
            const createWheelEventListener = callback => {
                let isScrolling;
                eventDispatcher.addEventListener(defines.EventNames.wtWheelEvent, (e: events.Event) => {
                    console.log('Wheel Event');
                    // 画角内判定
                    ThreeView.get().CameraTarget.getTargets(ThreeView.get().wcCameras.camera.me, ThreeView.get().OctreeManager.nodeStatus);
                    if (e.value != undefined) {
                        clearTimeout(isScrolling);
                        isScrolling = setTimeout(() => {
                          callback(e);
                        }, 300);
                    }
                });
            }

            createWheelEventListener((e)=>{
                console.log('Wheel End');
                // pixelstreamingカメラ送信
                new PSSceneManagement().sendCamera();
            });

            eventDispatcher.addEventListener(defines.EventNames.wtKeyEvent, (e: events.Event) => {
                console.log('Key Event');

                //2DでDELキー押下で実行されるので、防止
                if( marietta.webcad.WebCADGlobal.dimensionMenu.mode == '2D' ){
                    console.log('2Dモードなので無視');
                    return;
                }
                // // 3Dでカメラピンが選択されている時
                if (ObjectType.isCameraPin(ThreeView.get().ItemDragManager.target)) {
                    console.log('select camera pin')
                    return;
                }

                const keycode = e.value;
                if (keycode === 38 // up
                    || keycode === 40 // down
                    || keycode === 39 // right
                    || keycode === 37 // left
                    || keycode === 83 // W
                    || keycode === 87 // S
                    || keycode === 68 // A
                    || keycode === 65 // D
                ) {
                    ThreeView.get().unSelectedObject();
                }
                if (keycode == 27) {
                    ThreeView.get().unSelectedObject();
                    ThreeView.get().closeRightMenu();
                }
            });
        }
    }

    /*
     * reset(override)
     */
    public reset(options: defines.Options): void {
        if(View3dGlobal.shared.keepCameraState)return;

        super.reset(options);

        //this.firstShow = true;
        //this.firstWalk = true;
        this.secondWalk = true;
        this._floor = 1;

        const orbit = this.getCamera(defines.CameraDefs.orbitCamNo);
        if (orbit != undefined) {
            if (this.firstShow) orbit.position.copy(webcadDefines.WebCadCameraDefs.initOrbitPos);
            else orbit.position.copy(webcadDefines.WebCadCameraDefs.initShowPos);
            const orbitCtrl = <OrbitCameraControl>(orbit.control);
            orbitCtrl.me.target = webcadDefines.WebCadCameraDefs.initWlkThTgt.clone();
        }
        const wrkth = this.getCamera(defines.CameraDefs.wlkthCamNo);
        if (wrkth != undefined) wrkth.position.copy(webcadDefines.WebCadCameraDefs.initWlkThPos);

        super.setCameraNo(defines.CameraDefs.orbitCamNo);
    }

    /*
     * skipPhase
     */
    public skipPhase() {
        this.firstShow = false;
        this.firstWalk = false;
        this.secondWalk = false;
    }

    /*
     * home(override)
     */
    public home(): void {
        super.home();

        if (this._current == defines.CameraDefs.wlkthCamNo) {
            const porch = ThreeView.get().wcItemmgr.warps.getFromName(webcadDefines.WarpPointNames.porch);
            wcCameraUtils.registerWarpPoint(this.camera, porch, 0.0, true);

            this.camera.notify(defines.EventNames.cameraReset, {});
            this.eventDispatcher.dispatchEvent(new events.Event(defines.EventNames.wtPointChanged, this.camera.position));
            this.eventDispatcher.dispatchEvent(defines.EventNames.wtAngleChanged);
            this.eventDispatcher.dispatchEvent(webcadDefines.WebCadEventNames.wtFloorChanged);
        }
    }

    /*
     * setCameraNo(override)
     */
    public setCameraNo(index: number): void {
        if (this.firstShow && (index == defines.CameraDefs.orbitCamNo)) {
            this.firstShow = false;
            this.doTween(this.fadeIn);
        }
        else if ((this.firstWalk || this.secondWalk) && (index == defines.CameraDefs.wlkthCamNo)) {
            ThreeView.get().resetFloorShow();
            if (this.firstWalk) {
                this.doTween(this.tweenAround);
                this.firstWalk = false;
            }
            else {
                super.setCameraNo(defines.CameraDefs.wlkthCamNo);
                this.doTween(this.tweenIn);
                this.secondWalk = false;
            }
        }
        else {
            super.setCameraNo(index);
            if (ThreeView.exist()) {
                ThreeView.get().resetFloorShow();
                ThreeView.get().resetOpacity(true);
                this.notifyChange(index);

                ThreeView.get().measuremanager.visibleMesure(index);

                //pixelstreaming
                ThreeView.get().pscamerasetup();
            }
        }
        if (index == defines.CameraDefs.orbitCamNo)
            View3dGlobal.shared.clearCameraState();
    }

    /*
     * notifyInit
     */
    public notifyChange(index: number): void {
        const cam: Camera = this.camera;
        if (index == defines.CameraDefs.wlkthCamNo) {
            //this.floor = 1;
            this.eventDispatcher.dispatchEvent(new events.Event(webcadDefines.WebCadEventNames.wtFloorChanged, this.floor));

            const param: any = {};
            param[webcadDefines.ParamNames.layerNumber] = this.floor;
            const ptMini: THREE.Vector2 = ThreeView.get().wcItemmgr.pt3DToPtMini(cam.position);
            param[webcadDefines.ParamNames.point2d] = { x: (ptMini != undefined) ? ptMini.x : 0, y: (ptMini != undefined) ? ptMini.y : 0 };
            const rtMini: number = ThreeView.get().wcItemmgr.dir3DToDirMini(cam.lookAt);
            param[webcadDefines.ParamNames.rotation2d] = rtMini;
            cam.notify(defines.EventNames.wtPointChanged, param);
            cam.notify(defines.EventNames.wtAngleChanged, param);
        }
        cam.notify(defines.EventNames.cameraReset, {});
    }

    public initCameraStatus(porch: WarpPoint, entre: WarpPoint): void {
        const orbitCam: Camera = this.getCamera(defines.CameraDefs.orbitCamNo);
        const wlkthCam: Camera = this.getCamera(defines.CameraDefs.wlkthCamNo);
        if ((porch != undefined) && (entre != undefined)) {
            const ocam: THREE.PerspectiveCamera = <THREE.PerspectiveCamera>(this.getCamera(defines.CameraDefs.orbitCamNo).me);
            const wcam: THREE.PerspectiveCamera = <THREE.PerspectiveCamera>(this.getCamera(defines.CameraDefs.wlkthCamNo).me);
            const wDist: number = porch.position.distanceTo(entre.position);
            const wvFOV: number = wcam.fov * Math.PI / 180;
            const height: number = 2 * Math.tan(wvFOV / 2) * wDist;
            const ovFOV: number = ocam.fov * Math.PI / 180;
            const oDist: number = height / (2 * Math.tan(ovFOV / 2));

            wcCameraUtils.registerWarpPoint(orbitCam, porch, oDist - wDist, false);
            wcCameraUtils.registerWarpPoint(wlkthCam, porch, 0.0, true);
            wcCameraUtils.registerWarpPoint(wlkthCam, entre, 0.0, false);
        }
    }
    /* walkThroughPointChanged
     * walkThroughのカメラ位置が変更された.
     */
    private walkThroughPointChanged(hitPosition: THREE.Vector3) {
        const cameras: WebCadCameras = ThreeView.get().wcCameras;
        cameras.floor = ThreeView.get().wcItemmgr.heightToFloor(hitPosition.y);

        const cam: Camera = cameras.getCamera(defines.CameraDefs.wlkthCamNo);
        const param: any = {};
        param[webcadDefines.ParamNames.hitPosition] = hitPosition;
        param[webcadDefines.ParamNames.layerNumber] = cameras.floor;
        const ptMini: THREE.Vector2 = ThreeView.get().wcItemmgr.pt3DToPtMini(hitPosition);
        param[webcadDefines.ParamNames.point2d] = { x: (ptMini != undefined) ? ptMini.x : 0, y: (ptMini != undefined) ? ptMini.y : 0 };
        cam.notify(webcadDefines.WebCadEventNames.wtFloorChanged, param);
        cam.notify(defines.EventNames.wtPointChanged, param);
    }
    /* orbitAngleChanged
     * orbitカメラ方向が変更された.
     */
    private orbitAngleChanged() {
        const cameras: WebCadCameras = ThreeView.get().wcCameras;
        const cam: Camera = cameras.getCamera(defines.CameraDefs.orbitCamNo);
        const param: {} = {};
        cam.notify(defines.EventNames.orAngleChanged, param);
    }
    /* walkThroughAngleChanged
     * walkThroughのカメラ方向が変更された.
     */
    private walkThroughAngleChanged() {
        const cameras: WebCadCameras = ThreeView.get().wcCameras;
        const cam: Camera = cameras.getCamera(defines.CameraDefs.wlkthCamNo);
        const param: any = {};
        const rtMini: number = ThreeView.get().wcItemmgr.dir3DToDirMini(cam.lookAt);
        param[webcadDefines.ParamNames.rotation2d] = rtMini;
        cam.notify(defines.EventNames.wtAngleChanged, param);
    }
    /* walkThroughFloorChanged
     * walkThroughの階が変更された.
     */
    private walkThroughFloorChanged() {
        const cameras: WebCadCameras = ThreeView.get().wcCameras;
        const cam: Camera = cameras.getCamera(defines.CameraDefs.wlkthCamNo);
        const param: any = {};
        param[webcadDefines.ParamNames.layerNumber] = cameras.floor;
        cam.notify(webcadDefines.WebCadEventNames.wtFloorChanged, param);
    }
    /* walkThroughFalling
     * walkThroughのカメラが急に高度を下げた.
     */
    private walkThroughFalling(fallTo: number) {
        const cameras: WebCadCameras = ThreeView.get().wcCameras;
        cameras.freeFall(cameras, fallTo);
    }
    /* sizeObjectVisible
     * sizeObjectの表示状態が変更された.
     */
    public sizeObjectVisible(vis: boolean) {
        const cameras: WebCadCameras = ThreeView.get().wcCameras;
        const param: {} = {};
        cameras.camera.notify(webcadDefines.WebCadEventNames.sizeObjectVisible, param);
    }
    /* changeWalkThroughFloor
     * walkThroughするフロアを変更する.
     */
    private changeWalkThroughFloor(floor: number) {
        const cam = this.getCamera(defines.CameraDefs.wlkthCamNo);
        cam.position.y = this.getDownHeightFloor(floor);
        this.floor = floor;

        this.eventDispatcher.dispatchEvent(new events.Event(webcadDefines.WebCadEventNames.wtFloorChanged, floor));
    }
    /* doTween
     * TWEEN開始.
     */
    public doTween(func: any) {
        ThreeView.get().opeMode = defines.OpeMode.animate;
        func(this);
    }
    /* endTween
     * TWEEN終了.
     */
    public endTween() {
        ThreeView.get().opeMode = defines.OpeMode.operate;
        ThreeView.get().cameras.camera.notify(defines.EventNames.cameraReset, {});
    }
    /* tweenCompleted
     * TWEEN完了後のコールバック.
     */
    private calc_dist(pos1: THREE.Vector3, pos2: THREE.Vector3): number {
        return Math.sqrt(Math.pow((pos1.x - pos2.x), 2) + Math.pow((pos1.y - pos2.y), 2) + Math.pow((pos1.z - pos2.z), 2));
    }
    private tweenCompleted(id: number, param?: any): void {
        if (id == TweenCompletion.tweenAroundFinished) {
            super.setCameraNo(defines.CameraDefs.wlkthCamNo);
            this.notifyChange(defines.CameraDefs.wlkthCamNo);
            this.doTween(this.tweenIn);
        }
        else if (id == TweenCompletion.tweenFadeInFinished) {       // 外観から内観に
            this.doTween(this.tweenShow);
        }
        else if ((id == TweenCompletion.tweenInFinished) || (id == TweenCompletion.tweenFreeFallFinished)) {
            this.endTween();
            this.notifyChange(defines.CameraDefs.wlkthCamNo);

            //家の周りを回転した後に玄関から家の中に入った後
            if (id == TweenCompletion.tweenInFinished) {
                const control: OrbitCameraControl = <OrbitCameraControl>(this.getCamera(defines.CameraDefs.orbitCamNo).control);
                control.me.target.set(0, 0, 0);

                // pixelstreaming
                ThreeView.get().pscamerasetup();
            }
        }
        else if (id == TweenCompletion.tweenLevitationFinished) {
            this.changeWalkThroughFloor(param);
            ThreeView.get().wcItemmgr.checkFacingWall(this, this.getCamera(defines.CameraDefs.wlkthCamNo)); // ugly..
        }
        else if (id == TweenCompletion.tweenCameraRotateFinished) {
            this.endTween();
            this.eventDispatcher.dispatchEvent(defines.EventNames.wtAngleChanged);
        }
        else if (id == TweenCompletion.tweenTransition) {
            /* stay operation mode */
        }
        else if (id == TweenCompletion.tweenShowFinished) {
            this.endTween();
            ThreeView.get().controller.controllerSetUp();
        }
        else {
            this.endTween();
        }
    }
    /* getTween
     * TWEEN生成.
     */
    private getTween(camera: Camera, cur: any, end: any, tf: number, cameras: WebCadCameras, id: number, param: any, easing: any, delay: number): TWEEN.Tween {

        const PSSendCamera = (camera_) => {
            // pixelstreamingカメラ送信
            new PSSceneManagement().sendCamera();
        }

        if (tf == undefined) tf = 1.0;
        if (delay == undefined) delay = 0.0;
        if (end.type == TweenType.rotate) {
            const center = new THREE.Vector3(end.cx, end.cy, end.cz);
            const stPt = new THREE.Vector3(cur.x, cur.y, cur.z);
            const edPt = new THREE.Vector3(end.x, end.y, end.z);
            const stParam = wcCameraUtils.calcTweenParam(center, stPt);
            const edParam = wcCameraUtils.calcTweenParam(center, edPt);
            if (Math.abs(stParam.y - edParam.y) < Math.PI * 1.5) edParam.y += Math.PI * 2;
            const dist = ((this.calc_dist(stPt, center) + this.calc_dist(edPt, center)) / 2.0) * Math.abs(stParam.y - edParam.y);
            return new WebCadTween(stParam, camera, cameras)
                .me.delay(delay)
                .to(edParam, Math.min(dist * 100, 3000) * tf)
                .easing(easing)
                .onUpdate(() => {
                        const pos2 = wcCameraUtils.calcPosition(center, stParam);
                        camera.position.set(pos2.x, pos2.y, pos2.z);

                        PSSendCamera(camera);
                    }
                )
                .onComplete(() => {
                        cameras.tweenCompleted(id, param);
                    }
                );
        }
        else if (end.type == TweenType.move) {
            const dist = this.calc_dist(cur, end);
            return new WebCadTween(cur, camera, cameras)
                .me.delay(delay)
                .to(end, Math.min(dist * 100, 3000) * tf)
                .easing(easing)
                .onUpdate(() => {
                        camera.position.set(cur.x, cur.y, cur.z);

                        PSSendCamera(camera);
                    }
                )
                .onComplete(() => {
                        cameras.tweenCompleted(id, param);
                    }
                );
        }
        else if (end.type == TweenType.lookAt) {
            return new WebCadTween(cur, camera, cameras)
                .me.delay(delay)
                .to(end, 1000 * tf)
                .easing(easing)
                .onUpdate(() => {
                        const pos2 = camera.position.clone();
                        const nxt = camera.lookAt.clone().set(cur.x, cur.y, cur.z);
                        camera.position.set(0, 0, 0);
                        nxt.y = 0;
                        nxt.normalize();
                        camera.lookAt = nxt;
                        camera.position = pos2;

                        PSSendCamera(camera);
                    }
                )
                .onComplete(() => {
                        cameras.tweenCompleted(id, param);
                    }
                );
        }
        else if (end.type == TweenType.target) {
            const control: OrbitCameraControl = <OrbitCameraControl>(camera.control);
            const curTarget = control.me.target.clone();
            return new WebCadTween(curTarget, camera, cameras)
                .me.delay(delay)
                .to(end, 1000 * tf)
                .easing(easing)
                .onUpdate(() => {
                        if (camera.control instanceof OrbitCameraControl) {
                            const control: OrbitCameraControl = <OrbitCameraControl>(camera.control);
                            control.me.target.set(curTarget.x, curTarget.y, curTarget.z);
                        }

                        PSSendCamera(camera);
                    }
                )
                .onComplete(() => {
                        cameras.tweenCompleted(id, param);
                    }
                );
        }
        else if (end.type == TweenType.fade) {
            return new WebCadTween(cur, camera, cameras)
                .me.delay(delay)
                .to(end, end.y * tf)
                .easing(easing)
                .onUpdate(() => {
                        ThreeView.get().container.style.opacity = cur.x.toString();

                        PSSendCamera(camera);
                    }
                )
                .onComplete(() => {
                        cameras.tweenCompleted(id, param);
                    }
                );
        }
        return undefined;
    }

    /* startChainedTween
     * 連結Tweenの実行.
     */
    private startChainedTween(transitions: tweenTransitiondata[]) {
        const tweens = [];
        for (let i = 0; i < transitions.length - 1; i++) {
            tweens[i] = this.getTween(this.camera, transitions[i], transitions[i + 1], transitions[i + 1].tFactor, this, transitions[i + 1].completed, transitions[i + 1].compParam, transitions[i + 1].easing, transitions[i + 1].delay);
        }
        for (let i = 1; i < tweens.length; i++) tweens[i - 1].chain(tweens[i]);
        tweens[0].start();
    }

    /*　fadeIn
     * 初回のアニメーション.
     */
    public fadeIn(cams: WebCadCameras): void {
        if (cams.no != defines.CameraDefs.orbitCamNo) cams.setCameraNo(defines.CameraDefs.orbitCamNo);

        const transitions: tweenTransitiondata[] = [
            { type: TweenType.fade, x: 0.0001, y: 0, z: 0, easing: TWEEN.Easing.Quadratic.InOut },
            { type: TweenType.fade, x: 1.0, y: 1500, z: 0, easing: TWEEN.Easing.Quadratic.InOut, completed: TweenCompletion.tweenFadeInFinished, tFactor: webcadDefines.TweenSettings.fadeInSpeedFactor }];
        cams.startChainedTween(transitions);
    }

    /*　tweenShow
     * 初回のアニメーション.
     */
    private tweenShow(cams: WebCadCameras): void {
        if (cams.no != defines.CameraDefs.orbitCamNo) cams.setCameraNo(defines.CameraDefs.orbitCamNo);
        const pos2 = cams.camera.position;

        const transitions: tweenTransitiondata[] = [
            { type: TweenType.move, x: pos2.x, y: pos2.y, z: pos2.z, easing: TWEEN.Easing.Quadratic.InOut },
            { type: TweenType.move, x: webcadDefines.WebCadCameraDefs.initShowPos.x, y: webcadDefines.WebCadCameraDefs.initShowPos.y, z: webcadDefines.WebCadCameraDefs.initShowPos.z, easing: TWEEN.Easing.Quadratic.InOut, completed: TweenCompletion.tweenShowFinished, tFactor: webcadDefines.TweenSettings.showSpeedFactor }];
        cams.startChainedTween(transitions);
    }

    /*　tweenAround
     * ウォークスルー前の周回.
     */
    private tweenAround(cams: WebCadCameras): void {
        if (cams.no != defines.CameraDefs.orbitCamNo) cams.setCameraNo(defines.CameraDefs.orbitCamNo);

        const startPos = cams.getCamera(defines.CameraDefs.orbitCamNo).position;
        const movePos = startPos.clone();
        movePos.y = webcadDefines.WebCadCameraDefs.wlkthTweenStartHeight;
        const dist = cams.calc_dist(startPos, webcadDefines.WebCadCameraDefs.centerPos);
        movePos.x = (startPos.x * webcadDefines.WebCadCameraDefs.wlkthTweenStartRadius) / dist;
        movePos.z = (startPos.z * webcadDefines.WebCadCameraDefs.wlkthTweenStartRadius) / dist;
        let porchPos = cams.getCamera(defines.CameraDefs.orbitCamNo).getSetting(webcadDefines.WarpPointNames.porch + webcadDefines.WarpPointNames.position);
        let porchTgt = cams.getCamera(defines.CameraDefs.orbitCamNo).getSetting(webcadDefines.WarpPointNames.porch + webcadDefines.WarpPointNames.target);
        if (porchPos == undefined) porchPos = webcadDefines.WebCadCameraDefs.initWlkThPos;
        if (porchTgt == undefined) porchTgt = webcadDefines.WebCadCameraDefs.initWlkThTgt;
        const transitions: tweenTransitiondata[] = [
            { type: TweenType.na, x: startPos.x, y: startPos.y, z: startPos.z, easing: TWEEN.Easing.Sinusoidal.InOut },
            { type: TweenType.move, x: movePos.x, y: movePos.y, z: movePos.z, easing: TWEEN.Easing.Sinusoidal.InOut, completed: TweenCompletion.tweenTransition, tFactor: webcadDefines.TweenSettings.preMoveSpeedFactor },
            { type: TweenType.rotate, x: porchPos.x, y: porchPos.y, z: porchPos.z, cx: webcadDefines.WebCadCameraDefs.centerPos.x, cy: webcadDefines.WebCadCameraDefs.centerPos.y, cz: webcadDefines.WebCadCameraDefs.centerPos.z, easing: TWEEN.Easing.Sinusoidal.InOut, completed: TweenCompletion.tweenTransition, tFactor: webcadDefines.TweenSettings.aroundSpeedFactor },
            { type: TweenType.target, x: porchTgt.x, y: porchTgt.y, z: porchTgt.z, easing: TWEEN.Easing.Sinusoidal.InOut, completed: TweenCompletion.tweenAroundFinished, tFactor: webcadDefines.TweenSettings.rotCamSpeedFactor }]; // TODO
        cams.startChainedTween(transitions);
    }

    /*　tweenIn
     * ウォークスルー家屋侵入.
     */
    private tweenIn(cams: WebCadCameras): void {
        if (cams.no != defines.CameraDefs.wlkthCamNo) {
            return;
        }

        let startPos = cams.camera.getSetting(webcadDefines.WarpPointNames.porch + webcadDefines.WarpPointNames.position);
        if (startPos == undefined) startPos = webcadDefines.WebCadCameraDefs.initWlkThPos;
        cams.camera.position.set(startPos.x, startPos.y, startPos.z);
        let entPos = cams.camera.getSetting(webcadDefines.WarpPointNames.entrance + webcadDefines.WarpPointNames.position);
        if (entPos == undefined) entPos = webcadDefines.WebCadCameraDefs.initWlkThPos;
        let dist = cams.calc_dist(startPos, entPos);

        const transitions: tweenTransitiondata[] = [
            { type: TweenType.na, x: startPos.x, y: startPos.y, z: startPos.z },
            { type: TweenType.move, x: entPos.x, y: entPos.y, z: entPos.z, easing: TWEEN.Easing.Sinusoidal.InOut, completed: TweenCompletion.tweenInFinished, tFactor: webcadDefines.TweenSettings.inSpeedFactor, delay: 700 }];
        cams.startChainedTween(transitions);
    }

    /*　tweenTo
     * 各階ブラウズ時のアニメーション.
     */
    public tweenTo(cams: WebCadCameras, floor: number, measureLined: boolean): void {
        if (cams.no != defines.CameraDefs.orbitCamNo) cams.setCameraNo(defines.CameraDefs.orbitCamNo);
        const pos2 = cams.camera.position;
        // workaround, TODO: rewrite tween for more flexibility.
        if (cams.camera.control instanceof OrbitCameraControl) {
            let control: OrbitCameraControl = <OrbitCameraControl>(cams.camera.control);
            control.me.target.set(0, 0, 0);
        }
        const height: number = ThreeView.get().wcItemmgr.calcFloorViewHeight(floor, webcadDefines.WebCadCameraDefs.floorViewPos, measureLined);
        const transitions: tweenTransitiondata[] = [
            { type: TweenType.na, x: pos2.x, y: pos2.y, z: pos2.z, easing: TWEEN.Easing.Sinusoidal.InOut },
            { type: TweenType.move, x: webcadDefines.WebCadCameraDefs.floorViewPos.x, y: height, z: webcadDefines.WebCadCameraDefs.floorViewPos.z, easing: TWEEN.Easing.Sinusoidal.InOut, completed: TweenCompletion.tweenToFinished, tFactor: webcadDefines.TweenSettings.toSpeedFactor }];
        cams.startChainedTween(transitions);
    }

    /*　cameraRotation
     * カメラの回転.
     */
    public cameraRotate(cam: Camera, to: THREE.Vector3): void {
        const fr = cam.lookAt;
        const transitions: tweenTransitiondata[] = [
            { type: TweenType.lookAt, x: fr.x, y: fr.y, z: fr.z, easing: TWEEN.Easing.Sinusoidal.InOut },
            { type: TweenType.lookAt, x: to.x, y: to.y, z: to.z, easing: TWEEN.Easing.Sinusoidal.InOut, completed: TweenCompletion.tweenCameraRotateFinished, tFactor: webcadDefines.TweenSettings.rotCamSpeedFactor }];
        this.startChainedTween(transitions);
    }

    /*　freeFall
     * 落下のアニメーション.
     */
    public freeFall(cams: WebCadCameras, to: number): void {
        if (cams.no != defines.CameraDefs.wlkthCamNo) cams.setCameraNo(defines.CameraDefs.wlkthCamNo);
        const pos2 = cams.camera.position;

        const transitions: tweenTransitiondata[] = [
            { type: TweenType.move, x: pos2.x, y: pos2.y, z: pos2.z, easing: TWEEN.Easing.Quadratic.InOut },
            { type: TweenType.move, x: pos2.x, y: to, z: pos2.z, easing: TWEEN.Easing.Quadratic.InOut, completed: TweenCompletion.tweenFreeFallFinished, tFactor: webcadDefines.TweenSettings.fallSpeedFactor }];
        cams.startChainedTween(transitions);
    }

    /*　levitation
     * 上昇のアニメーション.
     */
    public levitation(cams: WebCadCameras, floor: number): void {
        if (cams.no != defines.CameraDefs.wlkthCamNo) cams.setCameraNo(defines.CameraDefs.wlkthCamNo);
        const pos2 = cams.camera.position;
        const to: number = this.getDownHeightFloor(floor);

        const transitions: tweenTransitiondata[] = [
            { type: TweenType.move, x: pos2.x, y: pos2.y, z: pos2.z, easing: TWEEN.Easing.Quadratic.InOut },
            { type: TweenType.move, x: pos2.x, y: to, z: pos2.z, easing: TWEEN.Easing.Quadratic.InOut, completed: TweenCompletion.tweenLevitationFinished, compParam: floor, tFactor: webcadDefines.TweenSettings.levitationSpeedFactor }];
        cams.startChainedTween(transitions);
    }

    public getDownHeight(x: number, floorHeight: number, z: number): number {
        // raycast down&up
        const pos2 = new THREE.Vector3(x, floorHeight, z);
        const targets = View3dGlobal.shared.getTargetObjects(9);
        const dir = new THREE.Vector3(0, -1, 0);
        const down_ray = new THREE.Raycaster(pos2, dir);
        const down_objs = down_ray.intersectObjects(targets, true);
        if ((down_objs.length > 0) && ((down_objs[0].object.name == defines.DefNames.terrain) || (statsUtils.getNameTree(down_objs[0].object).match(/site-node/)))) {
            dir.y = 1;
            const uy_ray = new THREE.Raycaster(pos2, dir);
            const up_objs = uy_ray.intersectObjects(targets, true);
            if ((up_objs.length > 0) && (statsUtils.getNameTree(up_objs[0].object).match(/:str?.*_.*:/))) {
                floorHeight = up_objs[0].point.y + 0.1;
            }
        }
        return floorHeight + webcadDefines.WebCadCameraDefs.wlkthHeight;
    }
    public getDownHeightFloor(floor: number): number {
        return this.getDownHeight(this.camera.position.x, ThreeView.get().wcItemmgr.floorToHeight(floor), this.camera.position.z);
    }
}
